import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiArrowRight, FiInfo } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy | ManagedSite.net";
    
    const description = "Read the ManagedSite.net privacy policy covering website inquiries, contact form submissions, service communications and basic site usage information.";
    const title = "Privacy Policy | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  return (
    <div className="w-full bg-white">
      {/* Hero Section */}
      <section className="bg-[#FAF8F3] pt-24 pb-16 lg:pt-36 lg:pb-20 border-b border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-6">Legal</div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-[#17211E] mb-8">Privacy Policy</h1>
          <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium max-w-2xl mx-auto">
            This privacy policy explains how ManagedSite.net collects, uses and protects information when you use this website or contact us about agency services.
          </p>
          <div className="mt-8 text-sm font-bold text-[#17211E]/40 uppercase tracking-widest">
            Last updated: August 6, 2026
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#EAF1EC]/50 border border-[#3B7C6D]/10 p-8 rounded-2xl mb-16 flex items-start gap-4">
            <div className="text-[#174C3C] mt-1">
              <SafeIcon icon={FiInfo} className="w-6 h-6" />
            </div>
            <p className="text-lg font-bold text-[#174C3C]">
              This page provides general information about how ManagedSite.net handles information. It is not legal advice.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-16">
            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">1. Who We Are</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>ManagedSite.net is a service of MyGoto Brands, Inc. MyGoto is a related brand of the same company.</p>
                <p>Contact inquiries can be submitted through our <Link to="/contact" className="text-[#174C3C] underline font-bold">contact page</Link>.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">2. Information We Collect</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>We may collect information you voluntarily submit, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Agency or company name</li>
                  <li>Website URL</li>
                  <li>Support or project details</li>
                  <li>Message contents</li>
                  <li>Other information you choose to provide</li>
                </ul>
                <p>We may also collect basic technical information such as browser type, device information, approximate location derived from IP address, pages visited, referring website, and date and time of visits.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">3. How We Use Information</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>We may use information to respond to inquiries, provide agency support and services, prepare proposals or onboarding details, improve the website and service communications, maintain security and prevent abuse, and meet operational and recordkeeping needs.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">4. Agency and Business Routing</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>ManagedSite.net works with agencies, white-label partners, and businesses. Information submitted through ManagedSite.net contact forms is handled by MyGoto Brands, Inc. as the operating company.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">5. Sharing of Information</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>We do not sell personal information. We may share information with trusted service providers who help operate the website, email communications, hosting, forms or business operations, only as needed to provide services. We may also disclose information if required by law or to protect rights, security or integrity of the service.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">6. Form Submissions</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>Contact form submissions on this website may be processed through Netlify Forms or similar form handling infrastructure used by the website host. Submitted information is used to respond to the inquiry and related service communication.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">7. Cookies and Analytics</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>This website may use cookies or similar technologies for basic functionality, preferences or analytics. You can control cookies through your browser settings.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">8. Data Retention</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>We keep information only as long as needed for the purpose collected, service delivery, legitimate business needs or legal requirements.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">9. Data Security</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>We take reasonable administrative and technical measures to protect information. No method of transmission or storage is completely secure.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">10. Your Choices</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>You may request updates to or deletion of contact information you submitted, subject to reasonable verification and any legal or operational retention needs. To make a request, use our <Link to="/contact" className="text-[#174C3C] underline font-bold">contact page</Link>.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">11. Third-Party Links</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>This website may link to third-party sites. We are not responsible for the privacy practices of third-party websites.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">12. Children’s Privacy</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>This website is intended for business and agency use. It is not directed to children.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">13. Policy Updates</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>We may update this privacy policy from time to time. The updated version will be posted on this page with a revised date.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">14. Contact</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>For privacy questions, contact ManagedSite.net through our <Link to="/contact" className="text-[#174C3C] underline font-bold">contact page</Link>.</p>
                <p className="font-bold">ManagedSite.net is a service of MyGoto Brands, Inc.</p>
              </div>
            </section>
          </div>

          <div className="mt-20 pt-10 border-t border-[#EAF1EC] flex flex-col sm:flex-row justify-between items-center gap-8">
            <Link to="/contact" className="home-button">
              Contact Us
            </Link>
            <Link to="/terms" className="text-[#174C3C] font-bold hover:text-[#3B7C6D] flex items-center gap-2">
              View Terms of Service <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;